import React, { useState, useEffect, useCallback } from 'react';
import {
  ActivityTask,
  TaskHazardAssessment,
  HazardCatalogItem,
  fetchHazardCatalog,
  fetchTaskHazards,
  createTaskHazard,
  deleteTaskHazard,
} from '../lib/gestion-tecnica-constructor';
import {
  calcularIPER,
  NIVELES_DEFICIENCIA,
  NIVELES_EXPOSICION,
  NIVELES_CONSECUENCIA,
  CATEGORIAS_PELIGRO,
  RIESGO_COLORS,
} from '../lib/iperCalculations';

interface Props {
  companyId: string;
  positionId: string | null;
  processName: string;
  activityName: string;
  task: ActivityTask;
  onClose: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const EMPTY_FORM = {
  hazard_category: '',
  hazard_type: '',
  hazard_type_other: '',
  hazard_description: '',
  possible_effects: '',
  control_source: '',
  control_medium: '',
  control_individual: '',
  nivel_deficiencia: '',
  nivel_exposicion: '',
  nivel_consecuencia: '',
  medida_eliminacion: '',
  medida_sustitucion: '',
  medida_control_ingenieria: '',
  medida_control_administrativo: '',
  medida_epp: '',
};

export default function TaskHazardsModal({ companyId, positionId, processName, activityName, task, onClose }: Props) {
  const [hazards, setHazards] = useState<TaskHazardAssessment[]>([]);
  const [catalog, setCatalog] = useState<HazardCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [h, c] = await Promise.all([fetchTaskHazards(task.id), fetchHazardCatalog()]);
    setHazards(h);
    setCatalog(c);
    setLoading(false);
  }, [task.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredTypes = catalog.filter(c => c.category === form.hazard_category && c.hazard_type !== 'Otros');
  const liveCalc = form.nivel_deficiencia && form.nivel_exposicion && form.nivel_consecuencia
    ? calcularIPER(Number(form.nivel_deficiencia) as any, Number(form.nivel_exposicion) as any, Number(form.nivel_consecuencia) as any)
    : null;

  async function handleAdd() {
    if (!form.hazard_category || !form.hazard_type || !form.nivel_deficiencia || !form.nivel_exposicion || !form.nivel_consecuencia) {
      alert('Completa categoría, tipo de peligro, ND, NE y NC.');
      return;
    }
    setSaving(true);
    try {
      const finalType = form.hazard_type === '__otro__' ? form.hazard_type_other.trim() : form.hazard_type;
      await createTaskHazard({
        company_id: companyId,
        task_id: task.id,
        position_id: positionId,
        process_name: processName,
        activity: activityName,
        task: task.name,
        hazard_category: form.hazard_category,
        hazard_type: finalType,
        hazard_description: form.hazard_description.trim() || null,
        possible_effects: form.possible_effects.trim() || null,
        control_source: form.control_source.trim() || null,
        control_medium: form.control_medium.trim() || null,
        control_individual: form.control_individual.trim() || null,
        nivel_deficiencia: Number(form.nivel_deficiencia) as any,
        nivel_exposicion: Number(form.nivel_exposicion) as any,
        nivel_consecuencia: Number(form.nivel_consecuencia) as any,
        medida_eliminacion: form.medida_eliminacion.trim() || null,
        medida_sustitucion: form.medida_sustitucion.trim() || null,
        medida_control_ingenieria: form.medida_control_ingenieria.trim() || null,
        medida_control_administrativo: form.medida_control_administrativo.trim() || null,
        medida_epp: form.medida_epp.trim() || null,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadAll();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('¿Eliminar esta evaluación de riesgo?')) return;
    await deleteTaskHazard(id);
    await loadAll();
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', zIndex: 500, padding: '24px 16px', overflowY: 'auto',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: '#1e293b', border: '1px solid #334155', borderRadius: 16,
        width: '100%', maxWidth: 720, boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
        marginTop: 'auto', marginBottom: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' }}>
          <h2 style={{ margin: 0 }}>Peligros y riesgos — {task.name}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#e2e8f0', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {loading && <p>Cargando...</p>}

          {!loading && hazards.length === 0 && !showForm && (
            <p style={{ color: '#94a3b8' }}>Sin peligros identificados todavía para esta tarea.</p>
          )}

          {!loading && hazards.map(h => (
            <div key={h.id} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <strong>{CATEGORIAS_PELIGRO.find(c => c.value === h.hazard_category)?.label ?? h.hazard_category}</strong>
                  {' — '}{h.hazard_type}
                  {h.hazard_description && <p style={{ color: '#94a3b8', fontSize: 13, margin: '4px 0 0' }}>{h.hazard_description}</p>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 6, fontWeight: 700,
                    color: 'white', background: RIESGO_COLORS[
                      h.nivel_riesgo >= 600 ? 'I' : h.nivel_riesgo >= 150 ? 'II' : h.nivel_riesgo >= 40 ? 'III' : 'IV'
                    ],
                  }}>
                    NR {h.nivel_riesgo}
                  </span>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{h.aceptabilidad}</div>
                </div>
              </div>
              <button onClick={() => handleDelete(h.id)} style={{ marginTop: 8, background: 'transparent', border: '1px solid #334155', color: '#fca5a5', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                Eliminar
              </button>
            </div>
          ))}

          {!showForm && (
            <button onClick={() => setShowForm(true)} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', marginTop: 8 }}>
              + Identificar peligro
            </button>
          )}

          {showForm && (
            <div style={{ marginTop: 12, borderTop: '1px solid #334155', paddingTop: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label>Categoría de peligro *</label>
                  <select style={inputStyle} value={form.hazard_category} onChange={e => setForm(f => ({ ...f, hazard_category: e.target.value, hazard_type: '' }))}>
                    <option value="">— Seleccionar —</option>
                    {CATEGORIAS_PELIGRO.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label>Tipo de peligro *</label>
                  <select style={inputStyle} value={form.hazard_type} disabled={!form.hazard_category} onChange={e => setForm(f => ({ ...f, hazard_type: e.target.value }))}>
                    <option value="">— Seleccionar categoría primero —</option>
                    {filteredTypes.map(t => <option key={t.id} value={t.hazard_type}>{t.hazard_type}</option>)}
                    {form.hazard_category && <option value="__otro__">Otros</option>}
                  </select>
                </div>
              </div>
              {form.hazard_type === '__otro__' && (
                <div style={{ marginTop: 10 }}>
                  <label>Especifica el tipo de peligro</label>
                  <input style={inputStyle} value={form.hazard_type_other} onChange={e => setForm(f => ({ ...f, hazard_type_other: e.target.value }))} />
                </div>
              )}

              <div style={{ marginTop: 10 }}>
                <label>Descripción del peligro</label>
                <textarea rows={2} style={inputStyle} value={form.hazard_description} onChange={e => setForm(f => ({ ...f, hazard_description: e.target.value }))} />
              </div>
              <div style={{ marginTop: 10 }}>
                <label>Efectos posibles</label>
                <textarea rows={2} style={inputStyle} value={form.possible_effects} onChange={e => setForm(f => ({ ...f, possible_effects: e.target.value }))} />
              </div>

              <h4 style={{ marginTop: 16, marginBottom: 4 }}>Controles existentes</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div><label>Fuente</label><textarea rows={2} style={inputStyle} value={form.control_source} onChange={e => setForm(f => ({ ...f, control_source: e.target.value }))} /></div>
                <div><label>Medio</label><textarea rows={2} style={inputStyle} value={form.control_medium} onChange={e => setForm(f => ({ ...f, control_medium: e.target.value }))} /></div>
                <div><label>Individuo</label><textarea rows={2} style={inputStyle} value={form.control_individual} onChange={e => setForm(f => ({ ...f, control_individual: e.target.value }))} /></div>
              </div>

              <h4 style={{ marginTop: 16, marginBottom: 4 }}>Evaluación GTC-45</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label>Nivel de Deficiencia (ND) *</label>
                  <select style={inputStyle} value={form.nivel_deficiencia} onChange={e => setForm(f => ({ ...f, nivel_deficiencia: e.target.value }))}>
                    <option value="">— Seleccionar —</option>
                    {NIVELES_DEFICIENCIA.map(n => <option key={n.value} value={String(n.value)}>{n.label}</option>)}
                  </select>
                </div>
                <div>
                  <label>Nivel de Exposición (NE) *</label>
                  <select style={inputStyle} value={form.nivel_exposicion} onChange={e => setForm(f => ({ ...f, nivel_exposicion: e.target.value }))}>
                    <option value="">— Seleccionar —</option>
                    {NIVELES_EXPOSICION.map(n => <option key={n.value} value={String(n.value)}>{n.label}</option>)}
                  </select>
                </div>
                <div>
                  <label>Nivel de Consecuencia (NC) *</label>
                  <select style={inputStyle} value={form.nivel_consecuencia} onChange={e => setForm(f => ({ ...f, nivel_consecuencia: e.target.value }))}>
                    <option value="">— Seleccionar —</option>
                    {NIVELES_CONSECUENCIA.map(n => <option key={n.value} value={String(n.value)}>{n.label}</option>)}
                  </select>
                </div>
              </div>

              {liveCalc && (
                <div style={{
                  marginTop: 10, padding: 10, borderRadius: 8,
                  background: RIESGO_COLORS[liveCalc.nivel_riesgo_categoria] + '22',
                  border: `1px solid ${RIESGO_COLORS[liveCalc.nivel_riesgo_categoria]}`,
                  color: RIESGO_COLORS[liveCalc.nivel_riesgo_categoria],
                }}>
                  Nivel de riesgo: <strong>{liveCalc.nivel_riesgo}</strong> ({liveCalc.nivel_riesgo_categoria}) — {liveCalc.interpretacion_riesgo} ({liveCalc.aceptabilidad})
                </div>
              )}

              <h4 style={{ marginTop: 16, marginBottom: 4 }}>Medida de control propuesta (jerarquía)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label>Eliminación</label><textarea rows={2} style={inputStyle} value={form.medida_eliminacion} onChange={e => setForm(f => ({ ...f, medida_eliminacion: e.target.value }))} /></div>
                <div><label>Sustitución</label><textarea rows={2} style={inputStyle} value={form.medida_sustitucion} onChange={e => setForm(f => ({ ...f, medida_sustitucion: e.target.value }))} /></div>
                <div><label>Control de ingeniería</label><textarea rows={2} style={inputStyle} value={form.medida_control_ingenieria} onChange={e => setForm(f => ({ ...f, medida_control_ingenieria: e.target.value }))} /></div>
                <div><label>Control administrativo</label><textarea rows={2} style={inputStyle} value={form.medida_control_administrativo} onChange={e => setForm(f => ({ ...f, medida_control_administrativo: e.target.value }))} /></div>
                <div><label>EPP</label><textarea rows={2} style={inputStyle} value={form.medida_epp} onChange={e => setForm(f => ({ ...f, medida_epp: e.target.value }))} /></div>
              </div>

              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <button disabled={saving} onClick={handleAdd} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>
                  {saving ? 'Guardando...' : 'Guardar peligro'}
                </button>
                <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} style={{ background: 'transparent', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
